import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import { useDarkMode } from '@/lib/hooks/use-dark-mode';

interface ContextTransitionOverlayProps {
    isAnimating: boolean;
    sourceRect: DOMRect | null;
    targetRect: DOMRect | null;
    targetPercent: number;
    onAnimationComplete?: () => void;
}

/**
 * Renders a "ghost" overlay that animates from the passage text position
 * to the canon bar, creating a "zooming out to see the bigger picture" effect.
 * 
 * Uses a multi-stage animation with a "glowing essence" effect.
 */
export function ContextTransitionOverlay({
    isAnimating,
    sourceRect,
    targetRect,
    targetPercent,
    onAnimationComplete,
}: ContextTransitionOverlayProps) {
    const isDarkMode = useDarkMode();
    const [shouldRender, setShouldRender] = useState(false);
    const hasCompletedRef = useRef(false);

    // Calculate target X position based on percent position on the canon bar
    const targetX = targetRect
        ? targetRect.left + (targetRect.width * targetPercent / 100)
        : 0;
    const targetY = targetRect ? targetRect.top + targetRect.height / 2 : 0;

    // Animation duration constant - must match the transition duration below
    // 800ms (flight) + 400ms (buffer for cleanup)
    const ANIMATION_DURATION_MS = 1200;

    useEffect(() => {
        if (isAnimating && sourceRect && targetRect) {
            setShouldRender(true);
            hasCompletedRef.current = false;

            // Use a timer to guarantee the full animation duration
            // This is more reliable than onAnimationComplete which can fire early
            // when there are nested child animations
            const timer = setTimeout(() => {
                if (!hasCompletedRef.current) {
                    hasCompletedRef.current = true;
                    setShouldRender(false);
                    onAnimationComplete?.();
                }
            }, ANIMATION_DURATION_MS);

            return () => clearTimeout(timer);
        }
    }, [isAnimating, sourceRect, targetRect, onAnimationComplete]);

    if (!shouldRender || !sourceRect || !targetRect) {
        return null;
    }

    // Calculate movement deltas
    const ghostHeight = Math.min(sourceRect.height, 400);
    const deltaX = targetX - sourceRect.left - (sourceRect.width / 2);
    const deltaY = targetY - sourceRect.top - (ghostHeight / 2);

    return (
        <motion.div
            initial={{
                left: sourceRect.left,
                top: sourceRect.top,
                width: sourceRect.width,
                height: ghostHeight,
                opacity: 1,
                scale: 1,
                x: 0,
                y: 0,
            }}
            animate={{
                opacity: 0,
                scale: 0.1,
                x: deltaX,
                y: deltaY,
            }}
            transition={{
                duration: 0.8,
                ease: "easeOut",
            }}
            className="pointer-events-none overflow-hidden rounded-lg fixed"
            style={{
                zIndex: 9999,
                position: 'fixed',
                transformOrigin: 'center center',
                background: isDarkMode
                    ? 'linear-gradient(180deg, rgba(30,25,20,0.95) 0%, rgba(40,35,25,0.9) 100%)'
                    : 'linear-gradient(180deg, rgba(255,252,245,0.95) 0%, rgba(250,245,235,0.9) 100%)',
                border: `1px solid ${isDarkMode ? 'rgba(255,220,150,0.3)' : 'rgba(180,140,80,0.3)'}`,
                boxShadow: `0 0 40px ${isDarkMode ? 'rgba(255,220,150,0.5)' : 'rgba(200,160,100,0.4)'}`,
            }}
        >
            {/* Text representation lines */}
            <div className="absolute inset-0 flex flex-col gap-2 p-4 overflow-hidden">
                {[...Array(6)].map((_, i) => (
                    <div
                        key={i}
                        className="rounded-full"
                        style={{
                            height: '8px',
                            width: `${70 + (i % 3) * 10}%`,
                            background: isDarkMode
                                ? `rgba(255,240,200,${0.4 - i * 0.05})`
                                : `rgba(80,60,40,${0.25 - i * 0.03})`,
                        }}
                    />
                ))}
            </div>
        </motion.div>
    );
}

